import { useEffect, useMemo, useState } from 'react';

export const useImageLoaded = (
    image = null,
    filters = {},
    size = null,
): { url: string; loaded: boolean; error: boolean } => {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    const { src = null } = image || {};

    useEffect(() => {
        if (!src) {
            setLoaded(false);
            setError(false);
            return;
        }

        const img = new Image();
        img.src = src;

        const handleLoad = () => {
            setLoaded(true);
            setError(false);
        };

        const handleError = () => {
            setLoaded(false);
            setError(true);
        };

        img.addEventListener('load', handleLoad);
        img.addEventListener('error', handleError);

        return () => {
            img.removeEventListener('load', handleLoad);
            img.removeEventListener('error', handleError);
        };
    }, [src]);

    return { url: src, loaded, error };
};

export const useImageUrl = (image = null, filter = null, size = 'large'): string | null => {
    const imageUrl = useMemo(() => {
        const { url = null } = image || {};
        if (!url) {
            return null;
        }

        const parts = filter !== null ? [filter, size] : [size];
        const addon = filter !== null ? `-filters(${parts.join('-')})` : '';

        const urlParts = url.split('.');
        const extension = urlParts.pop();
        // Color filters are applied on photos: serve PNGs as JPEG, much lighter
        const formatExtension = filter !== null && extension.toLowerCase() === 'png' ? '.jpg' : '';
        return `${urlParts.join('.')}${addon}.${extension}${formatExtension}`;
    }, [image, filter, size]);
    return imageUrl;
};

export default { useImageUrl, useImageLoaded };
